import React from "react";
import { Box, Typography, Button, useTheme, alpha, Container } from "@mui/material";
import { useGo } from "@refinedev/core";
import HomeIcon from "@mui/icons-material/Home";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { SeoWrapper } from "../../components/seo/SeoWrapper";
import { useTranslation } from "../../contexts/LanguageContext";

export const ErrorPage: React.FC = () => {
    const theme = useTheme();
    const go = useGo();
    const { t } = useTranslation();

    return (
        <Container maxWidth="md">
            <SeoWrapper
                title={t("404 - Sayfa Bulunamadı | CollectoHub")}
                description={t("Aradığınız sayfa bulunamadı. CollectoHub ana sayfasına dönerek kataloğu, topluluğu ve koleksiyon araçlarını keşfedebilirsiniz.")}
                canonical="https://www.collectohub.com/404"
                noindex
            />
            <Box
                component="main"
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "calc(100vh - 120px)",
                    textAlign: "center",
                    p: 3,
                }}
            >
                <Box
                    sx={{
                        position: "relative",
                        mb: 4,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Typography
                        variant="h1"
                        sx={{
                            fontSize: { xs: "8rem", md: "12rem" },
                            fontWeight: 900,
                            opacity: 0.05,
                            lineHeight: 1,
                            position: "absolute",
                            zIndex: 0,
                        }}
                    >
                        404
                    </Typography>
                    <SearchOffIcon
                        sx={{
                            fontSize: { xs: 80, md: 120 },
                            color: theme.palette.primary.main,
                            zIndex: 1,
                            filter: `drop-shadow(0 10px 15px ${alpha(theme.palette.primary.main, 0.3)})`
                        }}
                    />
                </Box>

                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        color: theme.palette.text.primary,
                        mb: 2,
                        fontSize: { xs: "1.75rem", md: "2.5rem" },
                        letterSpacing: "-1px"
                    }}
                >
                    {t("Sayfa Bulunamadı")}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        color: theme.palette.text.secondary,
                        maxWidth: 500,
                        mb: 5,
                        fontSize: "1.1rem",
                        lineHeight: 1.6,
                    }}
                >
                    {t("Aradığınız sayfa taşınmış, silinmiş veya hiç var olmamış olabilir. Lütfen adresi kontrol edin veya ana sayfaya dönün.")}
                </Typography>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Button
                        variant="contained"
                        size="large"
                        startIcon={<HomeIcon />}
                        onClick={() => go({ to: "/" })}
                        sx={{
                            borderRadius: "14px",
                            px: 4,
                            py: 1.5,
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "1rem",
                            boxShadow: `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                            transition: "all 0.2s ease",
                            "&:hover": {
                                transform: "translateY(-3px)",
                                boxShadow: `0 12px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
                            },
                        }}
                    >
                        {t("Ana Sayfaya Dön")}
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        onClick={() => window.history.back()}
                        sx={{
                            borderRadius: "14px",
                            px: 4,
                            py: 1.5,
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: "1rem",
                        }}
                    >
                        {t("Geri Git")}
                    </Button>
                </Stack>
            </Box>
        </Container>
    );
};

const Stack = ({ children, direction, spacing, sx }: any) => {
    const theme = useTheme();

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: direction,
                gap: theme.spacing(spacing),
                alignItems: "center",
                justifyContent: "center",
                ...sx
            }}
        >
            {children}
        </Box>
    );
};

export default ErrorPage;
